// Costa Rica payroll calculation helpers
// Estados de asistencia y reglas de pago:
//   - "Presente"                → horas según entrada/salida (jornada detectada)
//   - "Día Libre"               → 8 h pagadas (descanso semanal)
//   - "Vacaciones" / "Licencia" → 8 h pagadas
//   - "Ausente"                 → 0 h
//   - "Incapacidad INS"         → 0 h (lo paga el INS)
//   - "Incapacidad CCSS"        → 8 h los primeros 3 días del período, luego 0 h
//
// Reglas de cálculo de horas (Código de Trabajo CR):
//   - Diurna   (06-19) → máx 8 h ord., hora = salario/30/8
//   - Mixta            → máx 7 h ord., hora = salario/30/7
//   - Nocturna (19-06) → máx 6 h ord., hora = salario/30/6
//   - Hora extra = hora_normal × 1.5
import { getEmpAt, getEmpValueAt } from "./empleadoVersioning";

export const ACTIVIDAD_JORNADAS_ESPECIALES = "servicios_jornadas_especiales";
export const LIMITE_SEMANAL_JORNADA_ESPECIAL = 48;

// Modalidad "Servicios con Jornadas especiales": solo empresas con esa actividad y planillas quincenales/mensuales.
export function aplicaJornada48(empresa, emp) {
  if (empresa?.actividad_grupo !== ACTIVIDAD_JORNADAS_ESPECIALES) return false;
  return String(emp?.frecuencia_pago || "Mensual").toLowerCase() !== "semanal";
}

function lunesDe(fecha) {
  const d = new Date(`${fecha}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

// Reparte horas trabajadas por semana (L–D): hasta 48 h ordinarias, el excedente son extras.
export function redistribuirJornada48(items) {
  const acc = {};
  const sorted = [...items].sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
  for (const { fecha, h } of sorted) {
    if (!fecha || (h.source !== "Presente" && h.source !== "Feriado")) continue;
    const wk = lunesDe(fecha);
    const prev = acc[wk] || 0;
    const total = (h.ord || 0) + (h.ext || 0);
    const ord = Math.min(total, Math.max(0, LIMITE_SEMANAL_JORNADA_ESPECIAL - prev));
    h.ord = +ord.toFixed(2);
    h.ext = +(total - ord).toFixed(2);
    h.jornada48 = true;
    acc[wk] = prev + total;
  }
  return items;
}

// Horas de una lista de registros; aplica la regla de 48 h semanales si corresponde.
export function computeRecordsHours(records, empleado, ccssDayMap = {}, jornada48 = false) {
  const items = records.map(r => ({ fecha: r.fecha, h: computeRecordHours(r, empleado, ccssDayMap) }));
  if (jornada48) redistribuirJornada48(items.filter(i => i.fecha && getEmpValueAt(empleado, "jornada_especial_48", i.fecha)));
  return items.map(i => i.h);
}

export const CCSS_RATE = 0.1083;
export const CCSS_PENSIONADO_RATE = 0.055;
export const CCSS_DIAS_PAGOS = 3; // días pagados por el patrón al inicio de la incapacidad CCSS
export const CCSS_HORAS_PAGAS_POR_DIA = 4; // medio día (4 h) por cada uno de los 3 primeros días

const HORAS_POR_JORNADA = { Diurna: 8, Mixta: 7, Nocturna: 6 };

export function horaNormal(salarioMensual, jornada = "Diurna", diasBase = 30) {
  const h = HORAS_POR_JORNADA[jornada] || 8;
  return (salarioMensual || 0) / (diasBase || 30) / h;
}

export function horaExtra(salarioMensual, jornada = "Diurna", diasBase = 30) {
  return horaNormal(salarioMensual, jornada, diasBase) * 1.5;
}

// Detecta jornada y horas a partir de entrada/salida (Art. 138)
// Costa Rica: Diurna 05:00–19:00 · Nocturna 19:00–05:00 · Mixta abarca parte de ambas
// Art. 138 CT con 5 min de gracia: si la jornada mixta incluye ≥ 3h35min en horario nocturno
// (19:00–05:00) se reclasifica como Nocturna (máx 6 h ord.).
export function detectJornadaAndHours(entrada, salida, almuerzo_paga) {
  if (!entrada || !salida) return { jornada: null, total: 0, ord: 0, ext: 0, ordLimit: 0, reclasificada: false };
  const [eh, em] = entrada.split(":").map(Number);
  const [sh, sm] = salida.split(":").map(Number);
  let start = eh * 60 + em;
  let end = sh * 60 + sm;
  if (end <= start) end += 24 * 60;

  // Ventanas diurnas [05:00, 19:00) replicadas día a día
  let diurnaMin = 0;
  for (let day = 0; day * 1440 < end; day++) {
    const dStart = day * 1440 + 5 * 60;
    const dEnd = day * 1440 + 19 * 60;
    diurnaMin += Math.max(0, Math.min(end, dEnd) - Math.max(start, dStart));
  }
  const totalMin = end - start;
  const nocturnaMin = totalMin - diurnaMin;

  // Umbral Art. 138 = 3h30min + 5 min de gracia = 215 min
  const ART138_UMBRAL_MIN = 215;

  let jornada, ordLimit, reclasificada = false;
  if (nocturnaMin === 0)              { jornada = "Diurna";   ordLimit = 8; }
  else if (diurnaMin === 0)           { jornada = "Nocturna"; ordLimit = 6; }
  else if (nocturnaMin >= ART138_UMBRAL_MIN) {
    jornada = "Nocturna"; ordLimit = 6; reclasificada = true;
  }
  else                                { jornada = "Mixta";    ordLimit = 7; }

  let workedMin = totalMin;
  if (!almuerzo_paga) workedMin -= 60;
  workedMin = Math.max(0, workedMin);
  const total = workedMin / 60;
  const ord = Math.min(ordLimit, total);
  const ext = Math.max(0, total - ordLimit);
  return { jornada, ordLimit, reclasificada, total: +total.toFixed(2), ord: +ord.toFixed(2), ext: +ext.toFixed(2) };
}

// Construye mapa de día-del-período para registros CCSS por empleado.
// Para cada (empleado, fecha) con estado CCSS, asigna el número de día (1-indexed)
// dentro del run contiguo al que pertenece esa fecha.
export function buildCCSSDayMap(records) {
  const byEmp = {};
  for (const r of records || []) {
    if (r.estado === "Incapacidad CCSS" && r.fecha) {
      if (!byEmp[r.empleado_id]) byEmp[r.empleado_id] = new Set();
      byEmp[r.empleado_id].add(r.fecha);
    }
  }
  const map = {};
  for (const [empId, set] of Object.entries(byEmp)) {
    const sorted = [...set].sort();
    let day = 0, prev = null;
    for (const f of sorted) {
      if (prev) {
        const diff = Math.round((new Date(f + "T00:00:00") - new Date(prev + "T00:00:00")) / 86400000);
        day = diff === 1 ? day + 1 : 1;
      } else {
        day = 1;
      }
      map[`${empId}|${f}`] = day;
      prev = f;
    }
  }
  return map;
}

// Calcula horas (ord/ext) para un registro de asistencia según su estado.
// Resuelve campos versionados del empleado (almuerzo_paga, jornada, etc.) a la fecha del registro
// para preservar el histórico anterior a cualquier cambio puntual.
export function computeRecordHours(record, empleado, ccssDayMap = {}) {
  const estado = record.estado || "Presente";
  const empAt = empleado && record?.fecha ? getEmpAt(empleado, record.fecha) : empleado;

  if (estado === "Día Libre" || estado === "Vacaciones" || estado === "Licencia") {
    return { jornada: "Diurna", ord: 8, ext: 0, total: 8, ordLimit: 8, paid: true, source: estado, ordMultiplier: 1, extMultiplier: 1 };
  }
  if (estado === "Ausente" || estado === "Incapacidad INS") {
    return { jornada: null, ord: 0, ext: 0, total: 0, ordLimit: 0, paid: false, source: estado, ordMultiplier: 1, extMultiplier: 1 };
  }
  if (estado === "Incapacidad CCSS") {
    const day = ccssDayMap[`${record.empleado_id}|${record.fecha}`] || 1;
    if (day <= CCSS_DIAS_PAGOS) {
      return { jornada: "Diurna", ord: CCSS_HORAS_PAGAS_POR_DIA, ext: 0, total: CCSS_HORAS_PAGAS_POR_DIA, ordLimit: 8, paid: true, ccssDay: day, source: "Incapacidad CCSS", ordMultiplier: 1, extMultiplier: 1 };
    }
    return { jornada: null, ord: 0, ext: 0, total: 0, ordLimit: 0, paid: false, ccssDay: day, source: "Incapacidad CCSS", ordMultiplier: 1, extMultiplier: 1 };
  }
  if (estado === "Feriado") {
    // Si trabajó (hay entrada/salida): pago doble del día.
    // Las extras se duplican o no según la configuración de la empresa (extrasDoblesFeriado).
    const calc = detectJornadaAndHours(record.entrada, record.salida, empAt?.almuerzo_paga);
    return {
      ...calc,
      total: (calc.ord || 0) + (calc.ext || 0),
      paid: true,
      source: "Feriado",
      ordMultiplier: 2,
      extMultiplier: 1, // se sobreescribe en computePayrollForEmployee con el opt extrasDoblesFeriado
    };
  }
  // Presente (default)
  const calc = detectJornadaAndHours(record.entrada, record.salida, empAt?.almuerzo_paga);
  return { ...calc, total: (calc.ord || 0) + (calc.ext || 0), paid: true, source: "Presente", ordMultiplier: 1, extMultiplier: 1 };
}

// Cálculo de planilla — itera asistencias, decide horas por estado, aplica tarifa por jornada
// Modelo de columnas (nueva versión):
//   - Hrs Ord (Diurna/Mixta/Nocturna): solo horas trabajadas registradas en asistencia (Presente + Feriado trabajado), tarifa 1x
//   - Hrs Ext (Diurna/Mixta/Nocturna): solo horas extras registradas en asistencia, tarifa 1.5x
//   - Día Libre: días + monto (8h × tarifa diurna)
//   - Feriado: días + monto del "pago doble" (1x adicional de las horas trabajadas en feriado; extras se duplican si extrasDoblesFeriado)
//   - Incapacidad: días + monto pago (CCSS paga 4h × 3 primeros días)
//   - Vacaciones: días + monto (8h × tarifa diurna)
//   - Licencia: días + monto (4h × tarifa diurna = 50% del patrono; el otro 50% lo paga CCSS — Art. 95 CT)
//   - Bonificaciones: cantidad + monto
//   - Bruto = montoOrd + montoExt + diaLibre + feriado + incapacidad + vacaciones + licencia + bonificaciones
//   - CCSS = % sobre (montoOrd + diaLibre + vacaciones + licencia + feriado + extras[si toggle] + bonificaciones[aplica_ccss])
//   - Neto = Bruto − CCSS − Rebajos
export function computePayrollForEmployee(empleado, asistencias, opts = {}) {
  const { incluirCCSS = true, rebajos = [], bonificaciones = [], extrasDoblesFeriado = false, ccssAplicaExtras = false, noHorasExtras = false, diasBase = 30, ccssRate = null, override = null, periodStart = null, periodEnd = null, jornada48 = false } = opts;
  // Fecha de ingreso a CCSS del empleado (opcional). Si está definida, la CCSS
  // solo aplica proporcionalmente a los días del período >= esa fecha.
  // Campos CCSS vigentes al cierre del período (versionado: pensionado / ccss_aplica).
  const empCcss = periodEnd ? getEmpAt(empleado, periodEnd) : empleado;
  const ccssFechaInicio = empCcss.fecha_ccss || empCcss.ccss_fecha_inicio || null;
  // Tasa CCSS efectiva: si el empleado está pensionado, usa la reducida (5.50% por defecto).
  // Tasa efectiva de CCSS. Reglas:
  //  - pensionado: se usa la tasa reducida (config empresa) o el default `CCSS_PENSIONADO_RATE`.
  //  - reportar_medio_tiempo: el rebajo de CCSS aplica al 50% de la tasa
  //    correspondiente. Aplica también a pensionados (mitad de la tasa
  //    reducida). El monto BRUTO se conserva completo — sólo la tasa se
  //    reduce a la mitad porque el trabajador cotiza medio tiempo.
  // `ccssRate` = tasa reducida de pensionados configurada en la empresa.
  let effectiveCCSSRate = empCcss.pensionado ? (ccssRate ?? CCSS_PENSIONADO_RATE) : CCSS_RATE;
  if (empCcss.reportar_medio_tiempo) effectiveCCSSRate = effectiveCCSSRate / 2;
  const salarioMensual = Number(empleado.salario_base) || 0;
  const ccssDayMap = buildCCSSDayMap(asistencias);
  const tarifaDiurnaOrd = horaNormal(salarioMensual, "Diurna", diasBase);

  // Desglose por jornada (horas trabajadas registradas en asistencia)
  const byJornadaOrd = { Diurna: { hrs: 0, monto: 0 }, Mixta: { hrs: 0, monto: 0 }, Nocturna: { hrs: 0, monto: 0 } };
  const byJornadaExt = { Diurna: { hrs: 0, monto: 0 }, Mixta: { hrs: 0, monto: 0 }, Nocturna: { hrs: 0, monto: 0 } };

  // Desglose por concepto
  const breakdown = {
    diaLibre:       { dias: 0, monto: 0 },
    vacaciones:     { dias: 0, monto: 0 },
    licencia:       { dias: 0, monto: 0 },
    incapacidadCCSS:{ dias: 0, diasPagos: 0, diasNoPagos: 0, monto: 0 },
    incapacidadINS: { dias: 0, monto: 0 },
    ausente:        { dias: 0 },
    feriado:        { dias: 0, monto: 0 }, // SOLO el pago doble adicional
    bonificaciones: { cantidad: 0, monto: 0, montoCCSS: 0 },
  };

  const hrsList = computeRecordsHours(asistencias, empleado, ccssDayMap, jornada48);
  // Montos cotizables devengados antes de la fecha de ingreso a CCSS (se excluyen de la base).
  let baseAntesCCSS = 0, extAntesCCSS = 0;
  for (const [idx, a] of asistencias.entries()) {
    const h = hrsList[idx];
    // Resolver salario y jornada vigentes al día del registro (versionado)
    const empAt = a?.fecha ? getEmpAt(empleado, a.fecha) : empleado;
    const salarioDia = Number(empAt.salario_base) || salarioMensual;
    const jornadaDia = h.jornada || empAt.jornada || "Diurna";
    const tarifaOrd = horaNormal(salarioDia, jornadaDia, diasBase);
    const tarifaExt = horaExtra(salarioDia, jornadaDia, diasBase);
    const tarifaDiurnaOrdDia = horaNormal(salarioDia, "Diurna", diasBase);
    if (ccssFechaInicio && a?.fecha && a.fecha < ccssFechaInicio) {
      const ext = noHorasExtras ? 0 : (h.ext || 0) * tarifaExt;
      if (h.source === "Día Libre" || h.source === "Vacaciones") baseAntesCCSS += h.ord * tarifaDiurnaOrdDia;
      else if (h.source === "Licencia") baseAntesCCSS += h.ord * tarifaDiurnaOrdDia * 0.5;
      else if (h.source === "Feriado") { baseAntesCCSS += h.ord * tarifaOrd * 2 + (extrasDoblesFeriado ? ext : 0); extAntesCCSS += ext; }
      else if (h.source !== "Incapacidad CCSS") { baseAntesCCSS += (h.ord || 0) * tarifaOrd; extAntesCCSS += ext; }
    }

    switch (h.source) {
      case "Día Libre":
        breakdown.diaLibre.dias++;
        breakdown.diaLibre.monto += h.ord * tarifaDiurnaOrdDia; // 8h × tarifa diurna
        break;
      case "Vacaciones":
        breakdown.vacaciones.dias++;
        breakdown.vacaciones.monto += h.ord * tarifaDiurnaOrdDia;
        break;
      case "Licencia":
        breakdown.licencia.dias++;
        // Art. 95 CT: durante licencia (maternidad/paternidad) el patrono paga el 50 %
        // y la CCSS aporta el otro 50 %. Nuestro sistema solo refleja la cuota patronal.
        breakdown.licencia.monto += h.ord * tarifaDiurnaOrdDia * 0.5;
        break;
      case "Incapacidad CCSS":
        breakdown.incapacidadCCSS.dias++;
        if (h.paid) {
          breakdown.incapacidadCCSS.diasPagos++;
          breakdown.incapacidadCCSS.monto += h.ord * tarifaDiurnaOrdDia; // 4h × tarifa diurna
        } else {
          breakdown.incapacidadCCSS.diasNoPagos++;
        }
        break;
      case "Incapacidad INS":
        breakdown.incapacidadINS.dias++;
        break;
      case "Ausente":
        breakdown.ausente.dias++;
        break;
      case "Feriado": {
        // El feriado puede o no estar trabajado.
        // Si hay horas trabajadas: van a byJornadaOrd/byJornadaExt y además se paga 1x adicional (pago doble) en feriado.monto
        if (byJornadaOrd[jornadaDia] && h.ord > 0) {
          byJornadaOrd[jornadaDia].hrs += h.ord;
          byJornadaOrd[jornadaDia].monto += h.ord * tarifaOrd;
        }
        if (byJornadaExt[jornadaDia] && h.ext > 0 && !noHorasExtras) {
          byJornadaExt[jornadaDia].hrs += h.ext;
          byJornadaExt[jornadaDia].monto += h.ext * tarifaExt;
        }
        breakdown.feriado.dias++;
        // Pago "doble" feriado = 1x adicional de horas ord trabajadas
        breakdown.feriado.monto += h.ord * tarifaOrd;
        if (extrasDoblesFeriado && !noHorasExtras) {
          // Extras del feriado se duplican: 1x adicional sobre las horas ext
          breakdown.feriado.monto += h.ext * tarifaExt;
        }
        break;
      }
      default: {
        // Presente: horas trabajadas registradas en asistencia
        if (byJornadaOrd[jornadaDia] && h.ord > 0) {
          byJornadaOrd[jornadaDia].hrs += h.ord;
          byJornadaOrd[jornadaDia].monto += h.ord * tarifaOrd;
        }
        if (byJornadaExt[jornadaDia] && h.ext > 0 && !noHorasExtras) {
          byJornadaExt[jornadaDia].hrs += h.ext;
          byJornadaExt[jornadaDia].monto += h.ext * tarifaExt;
        }
      }
    }
  }

  // Redondeo a 2 decimales (helper local — mantiene precisión en centavos)
  const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

  // Override manual (Planilla Editable) — sobrescribe las horas y montos
  // acumulados desde asistencia (Diurna/Mixta/Nocturna ord y ext) y el monto
  // "pago doble" del feriado. Además, para planillas retroactivas/históricas
  // permite sobrescribir manualmente los días de: Día Libre, Feriado (días
  // pagados adicionales), Incapacidad CCSS, Vacaciones y Licencia.
  if (override) {
    // Reset horas por jornada
    for (const j of Object.keys(byJornadaOrd)) {
      byJornadaOrd[j].hrs = 0; byJornadaOrd[j].monto = 0;
      byJornadaExt[j].hrs = 0; byJornadaExt[j].monto = 0;
    }
    // Reset pago doble feriado (por horas trabajadas en feriado)
    breakdown.feriado.monto = 0;
    for (const j of Object.keys(byJornadaOrd)) {
      const oh = Number(override?.ord?.[j]) || 0;
      const eh = Number(override?.ext?.[j]) || 0;
      if (oh > 0) {
        byJornadaOrd[j].hrs = oh;
        byJornadaOrd[j].monto = oh * horaNormal(salarioMensual, j, diasBase);
      }
      if (eh > 0) {
        byJornadaExt[j].hrs = eh;
        byJornadaExt[j].monto = eh * horaExtra(salarioMensual, j, diasBase);
      }
    }
    const fh = Number(override?.feriado) || 0;
    if (fh > 0) {
      // Pago "doble" del feriado = 1x adicional de horas trabajadas a tarifa diurna
      breakdown.feriado.monto = fh * horaNormal(salarioMensual, "Diurna", diasBase);
    }

    // ─── Días manuales (extended override para planillas históricas) ───
    // Sólo aplica si el override trae explícitamente alguno de estos campos.
    // Sobrescribe (no suma) los valores calculados desde asistencia.
    const hasDiasExt = ["dias_libres","dias_feriado","dias_incapacidad_ccss","dias_vacaciones","dias_licencia"]
      .some(k => override[k] !== undefined && override[k] !== null && override[k] !== "");
    if (hasDiasExt) {
      const dL   = Number(override.dias_libres) || 0;
      const dFer = Number(override.dias_feriado) || 0;
      const dInc = Number(override.dias_incapacidad_ccss) || 0;
      const dVac = Number(override.dias_vacaciones) || 0;
      const dLic = Number(override.dias_licencia) || 0;
      // Día Libre: 8h × tarifa diurna
      breakdown.diaLibre.dias = dL;
      breakdown.diaLibre.monto = dL * 8 * tarifaDiurnaOrd;
      // Feriado (pagado día completo, no trabajado): 8h × tarifa diurna
      // Se suma al monto de pago doble (por horas trabajadas) si existiera.
      breakdown.feriado.dias = dFer;
      breakdown.feriado.monto = (breakdown.feriado.monto || 0) + dFer * 8 * tarifaDiurnaOrd;
      // Incapacidad CCSS: pago patrono primeros 3 días × 4h × tarifa diurna
      breakdown.incapacidadCCSS.dias = dInc;
      const diasPagos = Math.min(dInc, CCSS_DIAS_PAGOS);
      breakdown.incapacidadCCSS.diasPagos = diasPagos;
      breakdown.incapacidadCCSS.diasNoPagos = Math.max(0, dInc - diasPagos);
      breakdown.incapacidadCCSS.monto = diasPagos * CCSS_HORAS_PAGAS_POR_DIA * tarifaDiurnaOrd;
      // Vacaciones: 8h × tarifa diurna
      breakdown.vacaciones.dias = dVac;
      breakdown.vacaciones.monto = dVac * 8 * tarifaDiurnaOrd;
      // Licencia: patrono 50% × 8h × tarifa diurna (Art. 95 CT)
      breakdown.licencia.dias = dLic;
      breakdown.licencia.monto = dLic * 8 * tarifaDiurnaOrd * 0.5;
    }
  }

  // Totales redondeados a 2 decimales (centavos)
  for (const k of Object.keys(byJornadaOrd)) byJornadaOrd[k].monto = r2(byJornadaOrd[k].monto);
  for (const k of Object.keys(byJornadaExt)) byJornadaExt[k].monto = r2(byJornadaExt[k].monto);

  // Puesto exento de horas extras (jefatura / confianza, Art. 143 CT)
  // Se anulan todas las horas extras acumuladas y el "doble" de extras en feriado.
  if (noHorasExtras) {
    for (const k of Object.keys(byJornadaExt)) {
      byJornadaExt[k].hrs = 0;
      byJornadaExt[k].monto = 0;
    }
  }

  breakdown.diaLibre.monto       = r2(breakdown.diaLibre.monto);
  breakdown.vacaciones.monto     = r2(breakdown.vacaciones.monto);
  breakdown.licencia.monto       = r2(breakdown.licencia.monto);
  breakdown.incapacidadCCSS.monto= r2(breakdown.incapacidadCCSS.monto);
  breakdown.feriado.monto        = r2(breakdown.feriado.monto);

  const hrsOrd = Object.values(byJornadaOrd).reduce((s, j) => s + j.hrs, 0);
  const hrsExt = Object.values(byJornadaExt).reduce((s, j) => s + j.hrs, 0);
  const montoOrd = Object.values(byJornadaOrd).reduce((s, j) => s + j.monto, 0);
  const montoExt = Object.values(byJornadaExt).reduce((s, j) => s + j.monto, 0);

  // Bonificaciones
  let bonifTotal = 0, bonifBaseCCSS = 0;
  for (const b of bonificaciones) {
    const m = Number(b.monto) || 0;
    if (!m) continue;
    bonifTotal += m;
    if (b.aplica_ccss) {
      bonifBaseCCSS += m;
      if (ccssFechaInicio && !override && b.fecha && b.fecha < ccssFechaInicio) baseAntesCCSS += m;
    }
    breakdown.bonificaciones.cantidad++;
    breakdown.bonificaciones.monto += m;
    if (b.aplica_ccss && empCcss.ccss_aplica) breakdown.bonificaciones.montoCCSS += r2(m * effectiveCCSSRate);
  }
  bonifTotal = r2(bonifTotal);

  // Salario bruto = suma de TODOS los conceptos pagados
  const totalBruto =
    montoOrd + montoExt +
    breakdown.diaLibre.monto +
    breakdown.feriado.monto +
    breakdown.incapacidadCCSS.monto +
    breakdown.vacaciones.monto +
    breakdown.licencia.monto +
    bonifTotal;

  // CCSS: aplica sobre salario base + conceptos asimilables a salario.
  // Incapacidad NO entra en la base CCSS (es subsidio).
  // Extras solo entran si la empresa marca el toggle ccss_aplica_extras.
  let baseSalarial =
    montoOrd +
    breakdown.diaLibre.monto +
    breakdown.feriado.monto +
    breakdown.vacaciones.monto +
    breakdown.licencia.monto;
  if (ccssAplicaExtras) baseSalarial += montoExt;
  const baseCCSSCompleta = (empCcss.ccss_aplica && incluirCCSS) ? (baseSalarial + bonifBaseCCSS) : 0;
  // Con registros día a día se excluye exactamente lo devengado antes de la fecha de ingreso a CCSS.
  const porRegistro = !!ccssFechaInicio && !override;
  const baseCCSS = porRegistro
    ? Math.max(0, baseCCSSCompleta - baseAntesCCSS - (ccssAplicaExtras ? extAntesCCSS : 0))
    : baseCCSSCompleta;
  let ccss = baseCCSS > 0 ? r2(baseCCSS * effectiveCCSSRate) : 0;
  // Fracción CCSS por fecha de ingreso: si el empleado ingresó a CCSS en medio
  // del período, la deducción se aplica proporcional a los días cubiertos.
  let ccssFraccion = porRegistro && baseCCSSCompleta > 0 ? baseCCSS / baseCCSSCompleta : 1;
  if (ccss > 0 && ccssFechaInicio && !porRegistro) {
    // Determinar inicio/fin del período: usar opts si vienen, de lo contrario
    // derivarlos del rango de fechas de la asistencia.
    let pStart = periodStart, pEnd = periodEnd;
    if (!pStart || !pEnd) {
      const fechas = asistencias.map(a => a.fecha).filter(Boolean).sort();
      if (fechas.length) { pStart = pStart || fechas[0]; pEnd = pEnd || fechas[fechas.length - 1]; }
    }
    if (pStart && pEnd) {
      const parseDay = (s) => Math.floor(new Date(`${s}T00:00:00Z`).getTime() / 86400000);
      const ini = parseDay(pStart), fin = parseDay(pEnd), fCcss = parseDay(ccssFechaInicio);
      const total = Math.max(1, fin - ini + 1);
      const cubierto = Math.max(0, fin - Math.max(ini, fCcss) + 1);
      ccssFraccion = Math.max(0, Math.min(1, cubierto / total));
      ccss = r2(ccss * ccssFraccion);
    }
  }

  const totalRebajosCalc = rebajos.reduce((s, r) => s + (Number(r.monto) || 0), 0);

  // Overrides manuales de CCSS y Rebajos (usados típicamente en planillas
  // históricas o editables). Si el valor viene definido (no undefined/null/""),
  // reemplaza al monto calculado.
  const ccssOverride = (override && override.ccss_override !== undefined && override.ccss_override !== null && override.ccss_override !== "")
    ? Math.max(0, Number(override.ccss_override) || 0) : null;
  const rebajosOverride = (override && override.rebajos_override !== undefined && override.rebajos_override !== null && override.rebajos_override !== "")
    ? Math.max(0, Number(override.rebajos_override) || 0) : null;
  if (ccssOverride !== null) ccss = r2(ccssOverride);
  const totalRebajos = rebajosOverride !== null ? r2(rebajosOverride) : totalRebajosCalc;
  const neto = totalBruto - ccss - totalRebajos;

  // Estructura "byJornada" antigua (ord/ext combinados) para compatibilidad con JornadaBreakdown existente
  const byJornada = {
    Diurna:   { ord: byJornadaOrd.Diurna.hrs,   ext: byJornadaExt.Diurna.hrs },
    Mixta:    { ord: byJornadaOrd.Mixta.hrs,    ext: byJornadaExt.Mixta.hrs },
    Nocturna: { ord: byJornadaOrd.Nocturna.hrs, ext: byJornadaExt.Nocturna.hrs },
  };

  return {
    diasTrab: asistencias.length,
    hrsOrd: +hrsOrd.toFixed(2),
    hrsExt: +hrsExt.toFixed(2),
    montoOrd, montoExt,
    byJornadaOrd, byJornadaExt, byJornada,
    breakdown, bonifTotal,
    totalBruto, ccss, ccssRate: effectiveCCSSRate, ccssFraccion, ccssBase: r2(baseCCSS), totalRebajos, neto,
  };
}

export function ausenciasEnRango(empleado, asistencias, fechaInicio, fechaFin) {
  const set = new Set(asistencias.filter(a => a.estado !== "Ausente").map(a => a.fecha));
  let count = 0;
  const d = new Date(fechaInicio + "T00:00:00");
  const end = new Date(fechaFin + "T00:00:00");
  while (d <= end) {
    if (d.getDay() !== 0 && d.getDay() !== 6) {
      const iso = d.toISOString().slice(0, 10);
      if (!set.has(iso)) count++;
    }
    d.setDate(d.getDate() + 1);
  }
  return count;
}

// ---- Salario dividido en dos cuentas ----
// emp.salario_cuentas = { activo, c1_nombre, c1_monto, c2_nombre, c2_monto } (versionado)
export function cuentasSalario(emp, fecha) {
  const sc = fecha ? getEmpValueAt(emp, "salario_cuentas", fecha) : emp?.salario_cuentas;
  if (!sc?.activo) return null;
  const m1 = Number(sc.c1_monto) || 0;
  const m2 = Number(sc.c2_monto) || 0;
  if (m1 + m2 <= 0) return null;
  return {
    pct1: m1 / (m1 + m2),
    cuentas: [
      { nombre: sc.c1_nombre || "Cuenta 1", monto: m1 },
      { nombre: sc.c2_nombre || "Cuenta 2", monto: m2 },
    ],
  };
}

// Reparte valores por porcentaje; la cuenta 2 absorbe el redondeo. `derive` recalcula la fila 1 (p.ej. neto).
export function repartirValores(vals, pct1, derive) {
  const rd = (x) => Math.round((Number(x) || 0) * 100) / 100;
  const num = (v) => typeof v === "number";
  let a = vals.map(v => num(v) ? rd(v * pct1) : v);
  if (derive) a = derive(a);
  return [a, vals.map((v, i) => num(v) ? rd(v - a[i]) : v)];
}
